import type { BadgeTone } from '@wundo/ui';
import type { User } from '@/types/users.ts';

/** Where Wundo reaches this person day to day. */
export const CHANNEL_LABELS: Record<User['channel'], string> = {
  msteams: 'Microsoft Teams',
  slack: 'Slack',
  web: 'Web',
};

/** Where Wundo rings this person about a meeting, in words. */
export const NOTIFICATION_CHANNEL_LABELS: Record<User['notification_channel'], string> = {
  msteams: 'Microsoft Teams',
  slack: 'Slack',
  email: 'E-mail',
  none: 'None',
};

/** Why somebody stopped being active. Only ever set alongside `inactive`. */
const INACTIVE_REASON_LABELS: Record<string, string> = {
  unresponsive: 'Unresponsive',
  teams_blocked: 'Blocked in Teams',
  manual: 'Manually deactivated',
};

export function inactiveReasonLabel(reason: string): string {
  return INACTIVE_REASON_LABELS[reason] ?? reason;
}

export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const ROLE_TONES: Record<User['role'], BadgeTone> = {
  superadmin: 'warning',
  admin: 'info',
  user: 'neutral',
  guest: 'neutral',
  service: 'neutral',
};

export function roleTone(role: User['role']): BadgeTone {
  return ROLE_TONES[role] ?? 'neutral';
}

const CHANNEL_TONES: Record<User['channel'], BadgeTone> = {
  web: 'neutral',
  msteams: 'info',
  slack: 'warning',
};

export function channelTone(channel: User['channel']): BadgeTone {
  return CHANNEL_TONES[channel] ?? 'neutral';
}

const NOTIFICATION_CHANNEL_TONES: Record<User['notification_channel'], BadgeTone> = {
  msteams: 'info',
  slack: 'warning',
  email: 'success',
  none: 'quiet',
};

export function notificationChannelTone(
  channel: User['notification_channel'],
): BadgeTone {
  return NOTIFICATION_CHANNEL_TONES[channel] ?? 'quiet';
}

export function statusTone(status: string): BadgeTone {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'completed':
      return 'info';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}
